/**
 * Vollständigkeitsprüfung des SiGe-Plans — reine Regeln, keine KI.
 *
 * Zwei Aufrufer: der Anlage-Assistent (Schritt „Prüfen & Freigeben") und das
 * Export-Gate im Editor. Beide brauchen dieselbe Antwort, deshalb liegt die
 * Prüfung hier als pure Funktion ohne React/Supabase und ist testbar.
 *
 * `blockierend` heißt: der Plan taugt in dieser Form nicht als Dokument
 * (leer, ohne Bauzeit, ohne Plan-Nr., ohne Notrufplan). Der Export wird
 * deswegen nicht verweigert — die Oberfläche warnt und lässt bewusst passieren.
 * Druckt der Plan ausdrücklich ohne Zeitachse (Planungsphase, Bauzeitenplan
 * liegt noch nicht vor), fehlt ohne Bauzeit nichts.
 */

import { Arbeitsschritt, SigeBlock, SigeProject, planHatZeitachse } from './types';
import { normalisiere } from '../../services/mitgeltendeDokumente';
import { ANHANG_2_NUMMERN, istAnhangIINummer, type AnhangIINummer } from '../../data/anhang2Bruecke';

export interface VollstaendigkeitBefund {
  /** Stabiler Schlüssel für Tests und Oberfläche. */
  id: string;
  text: string;
  blockierend: boolean;
  /**
   * Nur ein Hinweis (Gewicht 0), kein Mangel: die Oberfläche zeigt ihn als
   * Hinweis, und er verdrängt nicht „Alle Pflichtangaben liegen vor".
   */
  hinweis?: boolean;
  /** Betroffene Arbeitsschritte, sofern die Regel zählt. */
  anzahl?: number;
}

export interface VollstaendigkeitErgebnis {
  /** 0–100, gewichtet über alle Regeln. */
  score: number;
  /** Alle verletzten Regeln (enthält auch die blockierenden). */
  fehlend: VollstaendigkeitBefund[];
  /** Teilmenge von `fehlend`: Regeln, die einen Export fragwürdig machen. */
  blockierend: VollstaendigkeitBefund[];
  /** IDs der erfüllten Regeln — für die Häkchenliste im Assistenten. */
  erfuellt: string[];
}

export interface VollstaendigkeitEingabe {
  plan?: Partial<SigeProject> | null;
  arbeitsschritte?: (Pick<Arbeitsschritt, 'bezeichnung' | 'hauptabschnitt' | 'schutzmassnahmen'> & Partial<Pick<Arbeitsschritt, 'gewerk'>>)[];
  bloecke?: Pick<SigeBlock, 'typ'>[];
  /**
   * Anhang-II-Arbeiten (Nummern 1–10 BaustellV), die nach der Planungsphase
   * ZUTREFFEN (`steckbrief.anhang2.nummern.wert`). Nur die bejahten — eine
   * ungeklärte Nummer ist kein Befund im Plan. Leer oder fehlend: die Regel
   * wird nicht bewertet.
   */
  anhang2Nummern?: readonly number[];
}

/**
 * Stichworte je Anhang-II-Nummer, mit denen ein Arbeitsschritt (Bezeichnung
 * + Gewerk) als „passend" gilt — in `normalisiere`-Form (klein, ae/oe/ue/ss).
 * Ein Stamm trifft auch innerhalb eines Worts; mit EINEM führenden
 * Leerzeichen nur am Wortanfang (so steckt „wasser" nicht in „Bauwasser").
 *
 * Bewusst konservativ: lieber ein Hinweis zu viel als ein Arbeitsschritt, der
 * fälschlich als Deckung einer gefährlichen Arbeit gilt. Die Regel wiegt 0 —
 * sie warnt, blockiert nicht. Kurztexte sind Benennungen der Nummern, kein
 * Vorschriftenwortlaut (der steht in `fragenkatalog.ts`).
 */
export const ANHANG2_STICHWORTE: Record<AnhangIINummer, { kurz: string; stichworte: string[] }> = {
  1: { kurz: 'Absturz über 7 m / Verschüttung über 5 m', stichworte: ['absturz', 'baugrube', 'graben', 'verbau', 'boeschung', 'verschuett'] },
  2: { kurz: 'Gefahr- und Biostoffe nach Nr. 2', stichworte: ['asbest', 'kmf', 'mineralfaser', 'pcb', 'pak', 'schadstoff', 'gefahrstoff', 'kontaminiert', 'biostoff', 'explosiv', 'karzinogen', 'krebserzeugend'] },
  3: { kurz: 'Ionisierende Strahlung', stichworte: ['strahlung', 'strahlen', 'roentgen', 'radiograph', 'kontrollbereich'] },
  4: { kurz: 'Hochspannungsleitungen', stichworte: ['freileitung', 'hochspannung', 'oberleitung', 'fahrleitung'] },
  5: { kurz: 'Ertrinkungsgefahr', stichworte: [' wasser', 'ertrink', 'schwimmend', 'gewaesser', 'hafen', 'wasserstrasse', 'ufer'] },
  6: { kurz: 'Brunnenbau, unterirdische Erdarbeiten, Tunnelbau', stichworte: ['tunnel', 'brunnen', 'unterirdisch', 'stollen', 'vortrieb', 'bergmaennisch'] },
  7: { kurz: 'Taucherarbeiten', stichworte: ['tauch'] },
  8: { kurz: 'Arbeiten in Druckluft', stichworte: ['druckluft', 'caisson', 'senkkasten'] },
  9: { kurz: 'Sprengarbeiten', stichworte: ['spreng'] },
  10: { kurz: 'Massivbauelemente mit kraftbetriebenen Hebezeugen', stichworte: ['fertigteil', 'massivbauelement', 'versetzger', 'elementmontage'] },
};

/** Trifft eines der Stichworte in einem der Texte (Bezeichnung, Gewerk)? */
const trifftStichwort = (texte: readonly string[], stichworte: readonly string[]): boolean => {
  // Mit Leerzeichen eingerahmt: ein Stamm mit führendem Leerzeichen trifft
  // so auch das erste Wort, aber nie mitten in einem Kompositum.
  const felder = texte.map(t => normalisiere(t || '')).filter(Boolean).map(f => ' ' + f + ' ');
  return stichworte.some(s => felder.some(f => f.includes(s)));
};

/**
 * Zutreffende Anhang-II-Nummern, zu denen KEIN Arbeitsschritt passt —
 * aufsteigend, ohne Doppelte; unbekannte Nummern werden ignoriert.
 */
export const anhang2OhneSchritt = (
  nummern: readonly number[] | null | undefined,
  schritte: readonly { bezeichnung: string; gewerk?: string }[],
): AnhangIINummer[] => {
  const gesucht = new Set((nummern || []).filter(istAnhangIINummer));
  return ANHANG_2_NUMMERN.filter(n =>
    gesucht.has(n) && !schritte.some(s => trifftStichwort([s?.bezeichnung, s?.gewerk || ''], ANHANG2_STICHWORTE[n].stichworte)));
};

/**
 * Ab hier wird eine Schutzmassnahme auf dem Plot zum Absatz: die Zeile
 * waechst, und damit das ganze Blatt. Ein Stichwort traegt dieselbe Aussage.
 */
export const PLOT_MASSNAHME_MAX_ZEICHEN = 80;
/**
 * Mehr Massnahmen je Schritt liest auf dem Aushang niemand mehr. Die
 * Mustervorlage hat bis zu 9 (Heissarbeiten) — sie darf nicht anschlagen.
 */
export const PLOT_MASSNAHMEN_MAX = 10;

interface Regel {
  id: string;
  gewicht: number;
  blockierend: boolean;
  hinweis?: boolean;
  /**
   * Regeln, die über die Arbeitsschritte zählen, sind ohne Arbeitsschritte
   * nicht bewertbar. Sie fallen dann ganz heraus (auch aus dem Nenner des
   * Scores) — sonst bekäme ein leerer Plan Punkte dafür, dass er nichts
   * Falsches enthält.
   */
  anwendbar?: (e: Required<VollstaendigkeitEingabe>) => boolean;
  /** null = erfüllt, sonst der Befundtext (optional mit Anzahl). */
  pruefe: (e: Required<VollstaendigkeitEingabe>) => { text: string; anzahl?: number } | null;
}

const REGELN: Regel[] = [
  {
    id: 'arbeitsschritte',
    gewicht: 30,
    blockierend: true,
    pruefe: e => (e.arbeitsschritte.length > 0 ? null : { text: 'Der Plan enthält keinen einzigen Arbeitsschritt.' }),
  },
  {
    id: 'bauzeit',
    gewicht: 20,
    blockierend: true,
    // Ohne Zeitachse (Haken „Bauzeitenplan liegt noch nicht vor") braucht der
    // Plan keine Bauzeit — die Regel fällt dann ganz heraus, auch aus dem Score.
    anwendbar: e => planHatZeitachse(e.plan?.darstellung),
    pruefe: e =>
      (e.plan?.zeitraum_von || '').trim() && (e.plan?.zeitraum_bis || '').trim()
        ? null
        : { text: 'Die Bauzeit (von / bis) ist nicht vollständig gesetzt; ohne sie fehlt die Zeitachse im Plan.' },
  },
  {
    // Nur ein Hinweis (Gewicht 0): ohne Zeitachse ist der Plan vollständig,
    // aber nicht fertig — die Zeiträume kommen mit der Fortschreibung.
    id: 'ohne_zeitachse',
    gewicht: 0,
    blockierend: false,
    hinweis: true,
    anwendbar: e => !planHatZeitachse(e.plan?.darstellung),
    pruefe: () => ({
      text: 'Der Plan druckt ohne Zeitachse (Bauzeitenplan liegt noch nicht vor): Zeitspalte ohne Balken und Daten. '
        + 'Liegt der Bauzeitenplan vor, den Haken im Plankopf entfernen und den Plan fortschreiben.',
    }),
  },
  {
    id: 'plan_nr',
    gewicht: 10,
    blockierend: true,
    pruefe: e => ((e.plan?.plan_nr || '').trim() ? null : { text: 'Es ist keine Plan-Nr. vergeben.' }),
  },
  {
    id: 'notrufplan',
    gewicht: 15,
    blockierend: true,
    pruefe: e =>
      e.bloecke.some(b => b?.typ === 'notrufplan')
        ? null
        : { text: 'Der Notrufplan-Block fehlt (Krankenhaus, Durchgangsarzt, Ersthelfer).' },
  },
  {
    id: 'ohne_abschnitt',
    gewicht: 10,
    blockierend: false,
    anwendbar: e => e.arbeitsschritte.length > 0,
    pruefe: e => {
      const anzahl = e.arbeitsschritte.filter(s => !(s?.hauptabschnitt || '').trim()).length;
      return anzahl === 0
        ? null
        : { text: `${anzahl} Arbeitsschritt(e) ohne Hauptabschnitt; sie fallen aus der Plan-Reihenfolge.`, anzahl };
    },
  },
  {
    id: 'ohne_massnahme',
    gewicht: 15,
    blockierend: false,
    anwendbar: e => e.arbeitsschritte.length > 0,
    pruefe: e => {
      const anzahl = e.arbeitsschritte.filter(s => !(s?.schutzmassnahmen || []).length).length;
      return anzahl === 0
        ? null
        : { text: `${anzahl} Arbeitsschritt(e) ohne Schutzmaßnahme.`, anzahl };
    },
  },
  {
    // Nur ein Hinweis (Gewicht 0): lange KI-Saetze sind kein Mangel des Plans,
    // machen den Plot aber lang und schwer lesbar.
    id: 'plot_lesbarkeit',
    gewicht: 0,
    blockierend: false,
    hinweis: true,
    anwendbar: e => e.arbeitsschritte.length > 0,
    pruefe: e => {
      const anzahl = e.arbeitsschritte.filter(s => {
        const m = s?.schutzmassnahmen || [];
        return m.length > PLOT_MASSNAHMEN_MAX || m.some(t => (t || '').trim().length > PLOT_MASSNAHME_MAX_ZEICHEN);
      }).length;
      return anzahl === 0
        ? null
        : {
          text: `${anzahl} Arbeitsschritt(e) mit sehr langen (über ${PLOT_MASSNAHME_MAX_ZEICHEN} Zeichen) oder sehr vielen `
            + `(über ${PLOT_MASSNAHMEN_MAX}) Schutzmaßnahmen. Stichworte halten den Plot kurz und lesbar.`,
          anzahl,
        };
    },
  },
  {
    // Nur ein Hinweis (Gewicht 0): die Planungsphase bejaht eine besonders
    // gefährliche Arbeit nach Anhang II, der Plan hat aber keine Zeile, die
    // nach ihren Stichworten dazu passt. Ob eine andere Zeile sie abdeckt,
    // entscheidet der Koordinator — deshalb kein Punktabzug.
    id: 'anhang2_ohne_schritt',
    gewicht: 0,
    blockierend: false,
    hinweis: true,
    anwendbar: e => e.anhang2Nummern.length > 0 && e.arbeitsschritte.length > 0,
    pruefe: e => {
      const offen = anhang2OhneSchritt(e.anhang2Nummern, e.arbeitsschritte);
      if (!offen.length) return null;
      const liste = offen.map(n => `Nr. ${n} (${ANHANG2_STICHWORTE[n].kurz})`).join(', ');
      return {
        text: `Anhang-II-Arbeit ${liste} ohne passenden Arbeitsschritt: nach den Merkmalen der Planungsphase `
          + 'zutreffend, im Plan aber keine Zeile mit passender Bezeichnung.',
        anzahl: offen.length,
      };
    },
  },
];

export const pruefeSigePlanVollstaendigkeit = (eingabe: VollstaendigkeitEingabe): VollstaendigkeitErgebnis => {
  const e: Required<VollstaendigkeitEingabe> = {
    plan: eingabe?.plan ?? null,
    arbeitsschritte: eingabe?.arbeitsschritte ?? [],
    bloecke: eingabe?.bloecke ?? [],
    anhang2Nummern: eingabe?.anhang2Nummern ?? [],
  };

  const fehlend: VollstaendigkeitBefund[] = [];
  const erfuellt: string[] = [];
  let punkte = 0;
  let gesamt = 0;

  REGELN.forEach(regel => {
    if (regel.anwendbar && !regel.anwendbar(e)) return;
    gesamt += regel.gewicht;
    const befund = regel.pruefe(e);
    if (!befund) {
      punkte += regel.gewicht;
      erfuellt.push(regel.id);
      return;
    }
    fehlend.push({
      id: regel.id, text: befund.text, blockierend: regel.blockierend, anzahl: befund.anzahl,
      ...(regel.hinweis ? { hinweis: true } : {}),
    });
  });

  return {
    score: gesamt > 0 ? Math.round((punkte / gesamt) * 100) : 0,
    fehlend,
    blockierend: fehlend.filter(f => f.blockierend),
    erfuellt,
  };
};

/** Kurzform für Statusanzeigen („Kein Plan" / „Entwurf · 12 Schritte"). */
export const beschreibePlanStand = (
  plan: Partial<SigeProject> | null | undefined,
  anzahlSchritte: number,
): string => {
  if (!plan) return 'Kein Plan';
  const status = plan.status === 'geprueft' ? 'Geprüft' : plan.status === 'ueberarbeitet' ? 'Überarbeitet' : 'Entwurf';
  return anzahlSchritte > 0 ? `${status} · ${anzahlSchritte} Schritte` : `${status} · leer`;
};
